import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

export interface Scrollability {
  scrollable: boolean;
  startOfScroll: boolean;
  endOfScroll: boolean;
}

export function useScrollability<T extends HTMLElement = HTMLDivElement>(): [
  React.RefCallback<T>,
  Scrollability,
  T | null,
] {
  const [element, setElement] = useState<T | null>(null);
  const ref = useCallback((node: T | null) => {
    setElement(node);
  }, []);
  const [scrollStatus, setScrollStatus] = useState<Scrollability>({
    scrollable: false,
    startOfScroll: false,
    endOfScroll: false,
  });

  const updateScrollStatus = useCallback(() => {
    if (element) {
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;
      const scrollTop = element.scrollTop;
      const endOfScroll =
        Math.ceil(scrollTop + clientHeight) >= scrollHeight - 5;

      setScrollStatus({
        scrollable: scrollHeight > clientHeight,
        startOfScroll: scrollTop === 0,
        endOfScroll,
      });
    }
  }, [element]);

  useEffect(() => {
    const updateWithRAF = () =>
      window.requestAnimationFrame(() =>
        window.setTimeout(() => updateScrollStatus()),
      );

    if (!element) {
      return;
    }

    updateScrollStatus();

    const resizeObserver = new ResizeObserver(updateWithRAF);
    resizeObserver.observe(element);

    const mutationObserver = new MutationObserver(updateWithRAF);
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [element, updateScrollStatus]);

  useEffect(() => {
    if (element) {
      element.addEventListener('scroll', updateScrollStatus);
      return () => element.removeEventListener('scroll', updateScrollStatus);
    }
  }, [element, updateScrollStatus]);

  useEffect(() => {
    const timer = setTimeout(updateScrollStatus, 125);
    return () => clearTimeout(timer);
  }, [updateScrollStatus]);

  return [ref, scrollStatus, element];
}
